import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocalStorage } from '@mantine/hooks';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Icons.
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';

import SkeletonBox from 'components/generic/SkeletonBox';
import { bloxColors } from 'themes/constants';
import { kpiBarExpandedMinHeight } from 'utility/constants';
import Dashboard, { type TDashboardFilters } from 'screens/dashboard/Dashboard';
import { useDashboard } from 'screens/dashboard/hooks/useDashboard';
import { ErrorBoundaryWithSuspense } from 'components/Layout/ErrorBoundaryWithSuspense';
import KpiBarFilters from './KpiBarFilters';
import KpiBarDashboardLink from './KpiBarDashboardLink';

type KpiBarProps = {
  collapsedText?: string;
  showBottomDivider?: boolean;
  dashboardId: string;
  storageKey: string;
  initialFilters?: TDashboardFilters;
};

export type KpiBarStorageState = { open: boolean | null };

// Default open as null to prevent layout shift as the hook's
// default value will be returned until the local storage value is pulled...
const KpiBarDefaultLSState: KpiBarStorageState = { open: true };

const KpiBar = ({
  collapsedText = 'KPIs',
  showBottomDivider = true,
  dashboardId,
  storageKey,
  initialFilters,
}: KpiBarProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Use the dashboard hook to fetch data
  const { dashboardFilters, setDashboardFilters, isReadyToRender, isLoading } =
    useDashboard({
      app: dashboardId,
      isChild: true,
    });

  // Apply initial filters if provided and different from current
  useEffect(() => {
    if (initialFilters && dashboardFilters && !isLoading) {
      const hasInitialFilters = Object.keys(initialFilters).length > 0;
      if (hasInitialFilters) {
        setDashboardFilters((prev) => ({
          ...prev,
          ...initialFilters,
        }));
      }
    }
    // Only run once on mount with initialFilters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ref for widgets container
  const widgetsContainerRef = useRef<HTMLDivElement>(null);

  // Local storage state for KPI bar open/close state preference.
  const [kpiBarState, setKpiBarState] = useLocalStorage<KpiBarStorageState>({
    key: storageKey,
    defaultValue: KpiBarDefaultLSState,
  });

  // maybe null if no pref / ls not pulled yet
  const isExpandedLs = kpiBarState?.open;

  // For some MUI props / conditionals
  const isExpandedCast = Boolean(kpiBarState?.open);

  const handleExpand = () => {
    setKpiBarState((prevState) => ({ open: !isExpandedLs }));
  };

  // Scroll state left/right for widgets container (for scroll faders)
  const [scrollState, setScrollState] = useState({
    atStart: true,
    atEnd: false,
  });

  // Update scroll state based on scroll position
  const updateScrollState = useCallback(() => {
    if (widgetsContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = widgetsContainerRef.current;

      const noScrollbar = scrollWidth <= clientWidth;

      setScrollState({
        atStart: noScrollbar || scrollLeft <= 4,
        atEnd: noScrollbar || scrollLeft + clientWidth >= scrollWidth - 4,
      });
    }
  }, []);

  // Listen for scroll events on widgets container
  useEffect(() => {
    const element = widgetsContainerRef.current;
    if (!element) return;

    element.addEventListener('scroll', updateScrollState, { passive: true });
    return () => {
      element.removeEventListener('scroll', updateScrollState);
    };
  }, [updateScrollState]);

  // Recalculate scroll state on resize window with layouteffect
  useLayoutEffect(() => {
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState]);

  const gapValue = 1;
  const kpiBgColor =
    theme.palette.mode === 'light' ? bloxColors.gray4 : bloxColors.offblack;

  const handleFiltersChange = useCallback(
    (newFilters: TDashboardFilters) => {
      setDashboardFilters((prev) => ({
        ...prev,
        ...newFilters,
      }));
    },
    [setDashboardFilters],
  );

  if (!kpiBarState || !dashboardId) {
    return (
      <SkeletonBox
        text="Loading..."
        width="100%"
        height="100%"
      />
    );
  }

  return (
    <ErrorBoundaryWithSuspense fullSize={false}>
      <Accordion
        className="kpi-bar--accordion"
        defaultExpanded={isExpandedCast}
        expanded={isExpandedCast}
        onChange={handleExpand}
        disableGutters={true}
        elevation={0}
        slotProps={{
          transition: {
            timeout: 0, // reduce layout shifting
          },
        }}
      >
        <AccordionSummary
          sx={{
            p: 0,
            m: 0,
            display: isExpandedCast ? 'none' : 'block',
            backgroundColor: kpiBgColor,
            minHeight: 'auto',
            borderBottom: showBottomDivider
              ? `1px solid ${theme.palette.divider}`
              : 'none',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              justifyContent: 'flex-end',
              margin: 0,
            },
          }}
        >
          <Button
            component="div"
            role="button"
            tabIndex={0}
            disableElevation
            startIcon={<ShowChartRoundedIcon />}
            onClick={handleExpand}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleExpand();
              }
            }}
            sx={{
              py: 2,
              pr: 2,
              flexGrow: 1,
              justifyContent: 'flex-end',
              borderRadius: 0,
            }}
          >
            {collapsedText}
          </Button>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            p: 0,
            borderBottom: showBottomDivider
              ? `1px solid ${theme.palette.divider}`
              : 'none',
            display: isExpandedLs ? 'block' : 'none',
            backgroundColor: kpiBgColor,
          }}
        >
          <Box
            data-testid="kpi-bar--container"
            className="kpi-bar--container"
            sx={{
              display: 'grid',
              gridTemplateAreas: `"actionsStart content actionsEnd"`,
              gridTemplateColumns: 'auto 1fr auto',
              gap: gapValue,
              alignItems: 'center',
              px: 1,
              // help reduce large layout shifts, match approx height kpi cards ~100px...
              minHeight: isExpandedLs ? kpiBarExpandedMinHeight : undefined,
            }}
          >
            <div
              data-testid="kpi-bar--actions-start"
              className="kpi-bar--actions-start"
            >
              <KpiBarFilters
                dashboardId={dashboardId}
                onFiltersChange={handleFiltersChange}
                initialFilters={initialFilters}
              />
            </div>

            {/* relative container for scroll faders */}
            <Box
              id="kpi-bar--scroll-faders-container"
              sx={{
                gridArea: 'content',
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <Box
                ref={widgetsContainerRef}
                className="kpi-bar--widgets"
                sx={{
                  overflow: 'auto',
                  width: '100%',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  '&::-webkit-scrollbar': {
                    height: '6px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.divider,
                    borderRadius: '3px',
                  },
                }}
              >
                {isLoading || !isReadyToRender ? (
                  <SkeletonBox
                    text="Loading..."
                    width="100%"
                    height={kpiBarExpandedMinHeight}
                  />
                ) : (
                  <Dashboard
                    app={dashboardId}
                    isChild={true}
                    isFullHeight={false}
                    filters={dashboardFilters ?? {}}
                    setDashboardFilters={setDashboardFilters}
                    isEditable={false}
                  />
                )}
              </Box>

              <Fragment>
                <Box
                  id="kpi-bar--scroll-fader-left"
                  sx={{
                    display: isExpandedLs ? 'block' : 'none',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: '15px',
                    background: `linear-gradient(to right, ${kpiBgColor} 0px, rgba(0, 0, 0, 0) 100%)`,
                    pointerEvents: 'none',
                    opacity: scrollState.atStart ? 0 : 1,
                    transition: 'opacity 0.15s',
                  }}
                />
                <Box
                  id="kpi-bar--scroll-fader-right"
                  sx={{
                    display: isExpandedLs ? 'block' : 'none',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    right: 0,
                    width: '15px',
                    background: `linear-gradient(to left, ${kpiBgColor} 0px, rgba(0, 0, 0, 0) 100%)`,
                    pointerEvents: 'none',
                    opacity: scrollState.atEnd ? 0 : 1,
                    transition: 'opacity 0.15s',
                  }}
                />
              </Fragment>
            </Box>

            <Stack
              className="kpi-bar--actions__end"
              direction={isMobileOrTablet ? 'column' : 'row'}
              spacing={isMobileOrTablet ? 1 : 0}
              sx={{
                alignItems: 'center',
                gridArea: 'actionsEnd',
                pr: isMobileOrTablet ? 0 : 1,
              }}
            >
              <KpiBarDashboardLink dashboardId={dashboardId} />
              <Box
                data-testid="kpi-bar--toggle"
                className="kpi-bar--toggle"
              >
                <IconButton onClick={handleExpand}>
                  <ExpandMoreRoundedIcon
                    sx={{
                      transform: `rotate(${!isExpandedLs ? '0' : '180deg'})`,
                      transition: 'transform 0.5s ease',
                    }}
                  />
                </IconButton>
              </Box>
            </Stack>
          </Box>
        </AccordionDetails>
      </Accordion>
    </ErrorBoundaryWithSuspense>
  );
};

export default KpiBar;
